import {FC, useEffect, useState} from 'react'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {Page, PageTranslation} from '../core/_models'
import clsx from 'clsx'
import {useListView} from '../core/ListViewProvider'
import {DataLoading} from '../../../../../_dashboard/partials/components/loading/DataLoading'
import {useQueryResponse} from '../core/QueryResponseProvider'
import {createOne, getAll, getOneByIdbylang, updateOne} from '../../../../services/common_requests'
import {useIntl} from 'react-intl'
import {useQueryRequest} from '../core/QueryRequestProvider'
import {toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {isNotEmpty} from '../../../../../_dashboard/helpers'
import TagsComponent from '../../../../../_dashboard/partials/widgets/tags/TagsComponent'
type Props = {
  isPageLoading: boolean
  Page: Page
  
}

const editPageSchema = Yup.object().shape({
  translations: Yup.array().of(
    Yup.object().shape({
      title: Yup.string()
        .min(3, 'Minimum 3 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('Title is required'),
      metaTitle: Yup.string()
      .min(3, 'Minimum 3 symbols')
        .max(60, 'Maximum 60 symbols'),
      metaDescription: Yup.string()
         .min(3, 'Minimum 3 symbols')
        .max(160, 'Maximum 160 symbols'),
       
      metaKeywords: Yup.array()
        .of(Yup.string().min(2, 'Minimum 2 symbols')),
      openGraphTitle: Yup.string()
        .max(100, 'Maximum 100 symbols'),
      openGraphDescription: Yup.string()
        .max(200, 'Maximum 200 symbols'),
      twitterTitle: Yup.string()
        .max(100, 'Maximum 100 symbols'),
      twitterDescription: Yup.string()
        .max(200, 'Maximum 200 symbols'),
    })
  ),
  slug: Yup.string()
    .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Invalid slug format')
    .min(3, 'Minimum 3 symbols')
    .required('Slug is required'),
})

const PageEditModalForm: FC<Props> = ({Page, isPageLoading}) => {
  const LANGUAGE_NAMES: {[key: string]: string} = {
    en: useIntl().formatMessage({id: 'COMMON.ENGLISH_LANGUAGE'}),
    fr: useIntl().formatMessage({id: 'COMMON.FRENSH_LANGUAGE'}),
    es: useIntl().formatMessage({id: 'COMMON.ESPANGOL_LANGUAGE'}),
    ar: useIntl().formatMessage({id: 'COMMON.ARABIC_LANGUAGE'}),
  }
  const {itemIdForUpdate, setItemIdForUpdate} = useListView()
  const {state} = useQueryRequest()
  const [query, setQuery] = useState<any>(state)
  const [languages, setLanguages] = useState<{code: string; name: string}[]>([])
  const [selectedLanguage, setSelectedLanguage] = useState('en')
  useEffect(() => {
    getAll(query, '/languages').then((data) => {
      if (data && Array.isArray(data)) {
        const formattedLanguages = data.map((code: string) => ({
          code,
          name: LANGUAGE_NAMES[code] || code.toUpperCase(),
        }))
        setLanguages(formattedLanguages)
      }
    })
  }, [query])

  useEffect(() => {
    if (itemIdForUpdate && selectedLanguage) {
      getOneByIdbylang(`/pages/${itemIdForUpdate}?language=${selectedLanguage}`)
        .then((data) => {
          if (data && data.translations) {
            const updatedTranslations = data.translations.map((t: any) => ({
              ...t,
              _id: t._id,
            }));

            formik.setValues({
              ...formik.values,
              translations: updatedTranslations,
            });
          }
        })
        .catch((error) => console.error('Error fetching translation:', error));
    }
  }, [itemIdForUpdate, selectedLanguage]);

  const {refetchData} = useQueryResponse()
  const intl = useIntl()
  const [PageForEdit] = useState<Page & {translations: PageTranslation[]}>({
    ...Page,
    translations: Page.translations?.length 
    ? Page.translations 
    : [{
        language: 'en',
        title: '',
        metaTitle: '',
        metaDescription: '',
        metaKeywords: [],
        openGraphType: '',
        openGraphTitle: '',
        openGraphDescription: '',
        openGraphImage: '',
        twitterTitle: '',
        twitterDescription: '',
        twitterImage: ''
      }]
  })

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetchData()
    }
    setItemIdForUpdate(undefined)
  }
  const formik = useFormik({
    initialValues: PageForEdit,
    validationSchema: editPageSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      if (isNotEmpty(itemIdForUpdate)) {
        const selectedTranslation = values.translations.find((t) => t.language === selectedLanguage)

        if (!selectedTranslation || !selectedTranslation._id) {
          toast.error(intl.formatMessage({id: 'ADMIN.COMMON.ERROR.ID_NOT_FOUND'}))
          return
        }
        const payload = {
          
            translations: values.translations.map((translation) => ({
              _id:selectedTranslation._id,
              language: translation.language || 'en',
              title: translation.title,
              metaTitle: translation.metaTitle,
              metaDescription: translation.metaDescription,
              metaKeywords: translation.metaKeywords,
              openGraphType: translation.openGraphType,
              openGraphTitle: translation.openGraphTitle,
              openGraphDescription: translation.openGraphDescription,
              openGraphImage: translation.openGraphImage,
              twitterTitle: translation.twitterTitle,
              twitterDescription: translation.twitterDescription,
              twitterImage: translation.twitterImage,
            })),
        }
        

        await updateOne(payload, `/pages/${itemIdForUpdate}`).then(
          (response) => {
            if (response?.payload?.code === 2021) {
              toast.success(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.SUCCESS.PAGE_UPDATED_SUCCESSFULLY',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
              cancel(true)
            } else if (response?.payload?.code === 4401) {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.ID_NOT_EXIST',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
            } else if (response?.payload?.code === 9100) {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.ID_NOT_FOUND',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
            } else {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_PAGE',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
            }
          }
        )
      } else {
        const payload = {
          slug: values.slug,
          title: values.translations[0].title,
          translations: values.translations.map((translation) => ({
            language: translation.language || 'en',
            title: translation.title,
            metaTitle: translation.metaTitle,
            metaDescription: translation.metaDescription,
            metaKeywords: translation.metaKeywords,
            openGraphType: translation.openGraphType,
            openGraphTitle: translation.openGraphTitle,
            openGraphDescription: translation.openGraphDescription,
            openGraphImage: translation.openGraphImage,
            twitterTitle: translation.twitterTitle,
            twitterDescription: translation.twitterDescription,
            twitterImage: translation.twitterImage,
          })),
        }
        try {
          await createOne(payload, '/pages').then((response) => {
            if (response?.payload?.code === 2021) {
              toast.success(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.SUCCESS.PAGES_ADDED_SUCCESSFULLY',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
              cancel(true)
            } else if (response?.payload?.code === 4400) {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.NAME_ALREADY_EXIST',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
            } else {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_PAGE',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
            }
          })
        } catch (ex) {
          console.error(ex)
        } finally {
          setSubmitting(false)
        }
      }
    },
  })
  const defaultTranslation: PageTranslation = {
    language: selectedLanguage ,
    title: '',
    metaTitle: '',
    metaDescription: '',
    metaKeywords: [],
    openGraphType: '',
    openGraphTitle: '',
    openGraphDescription: '',
    openGraphImage: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: ''
  };

  const selectedTranslation = formik.values.translations.find(t => t.language === selectedLanguage) || defaultTranslation;
  useEffect(() => {
    if (selectedTranslation) {
      formik.setFieldValue(`translations[${formik.values.translations.findIndex(t => t.language === selectedLanguage )}].metaKeywords`, selectedTranslation.metaKeywords);
    }
  }, [selectedLanguage]); 


  return (
    <>
      <form id='kt_modal_add_page_form' className='form' onSubmit={formik.handleSubmit} noValidate>
        {/* begin::Scroll */}
        <div
          className='d-flex flex-column scroll-y me-n15 pe-6'
          id='kt_modal_add_Page_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_page_header'
          data-kt-scroll-wrappers='#kt_modal_add_Page_scroll'
          data-kt-scroll-offset='300px'
        >
          {/* begin::Input group */}
          <div className='fv-row mb-5'>
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'COLUMNS.LANGUAGE'})}
            </label>
            {formik.values.translations.map((translation, index) => (
              <div key={index}>
                <select
                  id={`translations[${index}].language`}
                  name={`translations[${index}].language`}
                  onChange={(e) => {
                    formik.handleChange(e)
                    setSelectedLanguage(e.target.value)
                  }}
                  value={translation.language || ''}
                  className='form-select'
                >
                  <option value="" disabled hidden> {intl.formatMessage({id: 'COLUMNS.CHOOSE_LANGUAGE'})}</option>
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              
              </div>
            ))}
          </div>
          {/* end::Input group */}

          {/* begin::Input group */}
          <div className='fv-row mb-5 row'>
            {/* begin::Label */}
            <div className='col-12 col-md-4 mb-3 mb-md-0'>
              <label className='required fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.NAME'})}
              </label>
              {/* end::Label */}

              {/* begin::Input */}
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.NAME'})}
                value={selectedTranslation?.title || ''}
                onChange={(e) => {
                  const index = formik.values.translations.findIndex(
                    (t) => t.language === selectedLanguage
                  )
                  if (index !== -1) {
                    formik.setFieldValue(`translations[${index}].title`, e.target.value)
                    formik.setFieldTouched(`translations[${index}].title`, true)
                  }
                }}
                onBlur={formik.handleBlur}
                type='text'
                name='title'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid':
                      formik.touched.translations?.[0]?.title &&
                      (formik.errors.translations?.[0] as {title?: string})?.title,
                  },
                  {
                    'is-valid':
                      formik.touched.translations?.[0]?.title &&
                      !(formik.errors.translations?.[0] as {title?: string})?.title,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting || isPageLoading}
              />
              {formik.touched.translations?.[0]?.title && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>
                      {(formik.errors.translations?.[0] as {title?: string})?.title || ''}
                    </span>
                  </div>
                </div>
              )}
              {/* end::Input */}
            </div>
            <div className='col-12 col-md-4'>
              <label className='required fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.SLUG'})}
              </label>
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.SLUG'})}
                value={String(formik.values.slug) || ''}
                onChange={(e) => {
                  formik.setFieldValue('slug', e.target.value);
                  formik.setFieldTouched('slug', true);
                }}
                onBlur={formik.handleBlur}
                type='text'
                name='slug'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid': formik.touched.slug &&
                      (formik.errors.slug as {slug?: string})?.slug,
                  },
                  {
                    'is-valid': formik.touched.slug &&
                      !(formik.errors.slug as {slug?: string})?.slug,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting || isPageLoading}
              />
              {formik.touched.slug && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>
                      {formik.errors.slug ? String(formik.errors.slug) : ''}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className='col-12 col-md-4'>
              <label className='fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.METE_TITLE'})}
              </label>
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.METE_TITLE'})}
                value={selectedTranslation?.metaTitle || ''}
                onChange={(e) => {
                  const index = formik.values.translations.findIndex(
                    (t) => t.language === selectedLanguage
                  )
                  if (index !== -1) {
                    formik.setFieldValue(`translations[${index}].metaTitle`, e.target.value)
                  }
                }}
                type='text'
                name='metaTitle'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid':
                      formik.touched.translations?.[0]?.metaTitle &&
                      (formik.errors.translations?.[0] as {metaTitle?: string})?.metaTitle,
                  },
                  {
                    'is-valid':
                      formik.touched.translations?.[0]?.metaTitle &&
                      !(formik.errors.translations?.[0] as {metaTitle?: string})?.metaTitle,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting || isPageLoading}
              />
            </div>
          </div>
          {/* end::Input group */}

          {/* Nouvelle div pour metaDescription */}
          <div className='fv-row mb-5'>
            <label className='fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'COLUMNS.METE_DESCRIPTION'})}
            </label>
            <textarea
              placeholder={intl.formatMessage({id: 'COLUMNS.METE_DESCRIPTION'})}
              value={selectedTranslation?.metaDescription || ''}
              onChange={(e) => {
                const index = formik.values.translations.findIndex(
                  (t) => t.language === selectedLanguage
                )
                if (index !== -1) {
                  formik.setFieldValue(`translations[${index}].metaDescription`, e.target.value)
                }
              }}
              name='metaDescription'
              className={clsx(
                'form-control mb-3 mb-lg-0',
                {
                  'is-invalid':
                    formik.touched.translations?.[0]?.metaDescription &&
                    (formik.errors.translations?.[0] as {metaDescription?: string})
                      ?.metaDescription,
                },
                {
                  'is-valid':
                    formik.touched.translations?.[0]?.metaDescription &&
                    !(formik.errors.translations?.[0] as {metaDescription?: string})
                      ?.metaDescription,
                }
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || isPageLoading}
            />
          </div>

          <div className='fv-row mb-5 row'>
            {/* begin::Label */}
            <div className='col-12 col-md-4 mb-3 mb-md-0'>
              <label className='fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_TYPE'})}
              </label>
              {/* end::Label */}

              {/* begin::Input */}
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_TYPE'})}
                value={selectedTranslation?.openGraphType || ''}
                onChange={(e) => {
                  const index = formik.values.translations.findIndex(
                    (t) => t.language === selectedLanguage
                  )
                  if (index !== -1) {
                    formik.setFieldValue(`translations[${index}].openGraphType`, e.target.value)
                  }
                }}
                type='text'
                name='openGraphType'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid':
                      formik.touched.translations?.[0]?.openGraphType &&
                      (formik.errors.translations?.[0] as {openGraphType?: string})?.openGraphType,
                  },
                  {
                    'is-valid':
                      formik.touched.translations?.[0]?.openGraphType &&
                      !(formik.errors.translations?.[0] as {openGraphType?: string})?.openGraphType,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting || isPageLoading}
              />
            </div>
            <div className='col-12 col-md-4'>
              {/* begin::Label */}
              <label className='fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_TITLE'})}
              </label>
              {/* end::Label */}

              {/* begin::Input */}
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_TITLE'})}
                value={selectedTranslation?.openGraphTitle || ''}
                onChange={(e) => {
                  const index = formik.values.translations.findIndex(
                    (t) => t.language === selectedLanguage
                  )
                  if (index !== -1) {
                    formik.setFieldValue(`translations[${index}].openGraphTitle`, e.target.value)
                  }
                }}
                type='text'
                name='openGraphTitle'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid':
                      formik.touched.translations?.[0]?.openGraphTitle &&
                      (formik.errors.translations?.[0] as {openGraphTitle?: string})
                        ?.openGraphTitle,
                  },
                  {
                    'is-valid':
                      formik.touched.translations?.[0]?.openGraphTitle &&
                      !(formik.errors.translations?.[0] as {openGraphTitle?: string})
                        ?.openGraphTitle,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting || isPageLoading}
              />
            </div>
            <div className='col-12 col-md-4'>
              {/* begin::Label */}
              <label className='fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_IMAGE'})}
              </label>
              {/* end::Label */}

              {/* begin::Input */}
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_IMAGE'})}
                value={selectedTranslation?.openGraphImage || ''}
                onChange={(e) => {
                  const index = formik.values.translations.findIndex(
                    (t) => t.language === selectedLanguage
                  )
                  if (index !== -1) {
                    formik.setFieldValue(`translations[${index}].openGraphImage`, e.target.value)
                  }
                }}
                type='text'
                name='openGraphImage'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid':
                      formik.touched.translations?.[0]?.openGraphImage &&
                      (formik.errors.translations?.[0] as {openGraphImage?: string})
                        ?.openGraphImage,
                  },
                  {
                    'is-valid':
                      formik.touched.translations?.[0]?.openGraphImage &&
                      !(formik.errors.translations?.[0] as {openGraphImage?: string})
                        ?.openGraphImage,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting || isPageLoading}
              />
            </div>
          </div>

          {/* Nouvelle div pour Open Graph Description */}
          <div className='fv-row mb-5'>
            <label className='fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_DESCRIPTION'})}
            </label>
            <textarea
              placeholder={intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_DESCRIPTION'})}
              value={selectedTranslation?.openGraphDescription || ''}
              onChange={(e) => {
                const index = formik.values.translations.findIndex(
                  (t) => t.language === selectedLanguage
                )
                if (index !== -1) {
                  formik.setFieldValue(
                    `translations[${index}].openGraphDescription`,
                    e.target.value
                  )
                }
              }}
              name='openGraphDescription'
              className={clsx(
                'form-control mb-3 mb-lg-0',
                {
                  'is-invalid':
                    formik.touched.translations?.[0]?.openGraphDescription &&
                    (formik.errors.translations?.[0] as {openGraphDescription?: string})
                      ?.openGraphDescription,
                },
                {
                  'is-valid':
                    formik.touched.translations?.[0]?.openGraphDescription &&
                    !(formik.errors.translations?.[0] as {openGraphDescription?: string})
                      ?.openGraphDescription,
                }
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || isPageLoading}
            />
          </div>

          <div className='fv-row mb-5 row'>
            <div className='col-12 col-md-6 mb-3 mb-md-0'>
              {/* begin::Label */}
              <label className='fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.TWITTER_TITLE'})}
              </label>
              {/* end::Label */}

              {/* begin::Input */}
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.TWITTER_TITLE'})}
                value={selectedTranslation?.twitterTitle || ''}
                onChange={(e) => {
                  const index = formik.values.translations.findIndex(
                    (t) => t.language === selectedLanguage
                  )
                  if (index !== -1) {
                    formik.setFieldValue(`translations[${index}].twitterTitle`, e.target.value)
                  }
                }}
                type='text'
                name='twitterTitle'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid':
                      formik.touched.translations?.[0]?.twitterTitle &&
                      (formik.errors.translations?.[0] as {twitterTitle?: string})?.twitterTitle,
                  },
                  {
                    'is-valid':
                      formik.touched.translations?.[0]?.twitterTitle &&
                      !(formik.errors.translations?.[0] as {twitterTitle?: string})?.twitterTitle,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting || isPageLoading}
              />
            </div>
            <div className='col-12 col-md-6'>
              {/* begin::Label */}
              <label className='fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.TWITTER_IMAGE'})}
              </label>
              {/* end::Label */}

              {/* begin::Input */}
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.TWITTER_IMAGE'})}
                value={selectedTranslation?.twitterImage || ''}
                onChange={(e) => {
                  const index = formik.values.translations.findIndex(
                    (t) => t.language === selectedLanguage
                  )
                  if (index !== -1) {
                    formik.setFieldValue(`translations[${index}].twitterImage`, e.target.value)
                  }
                }}
                type='text'
                name='twitterImage'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid':
                      formik.touched.translations?.[0]?.twitterImage &&
                      (formik.errors.translations?.[0] as {twitterImage?: string})?.twitterImage,
                  },
                  {
                    'is-valid':
                      formik.touched.translations?.[0]?.twitterImage &&
                      !(formik.errors.translations?.[0] as {twitterImage?: string})?.twitterImage,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting || isPageLoading}
              />
            </div>
          </div>

          {/* Nouvelle div pour Twitter Description */}
          <div className='fv-row mb-5'>
            <label className='fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'COLUMNS.TWITTER_DESCRIPTION'})}
            </label>
            <textarea
              placeholder={intl.formatMessage({id: 'COLUMNS.TWITTER_DESCRIPTION'})}
              value={selectedTranslation?.twitterDescription || ''}
              onChange={(e) => {
                const index = formik.values.translations.findIndex(
                  (t) => t.language === selectedLanguage
                )
                if (index !== -1) {
                  formik.setFieldValue(
                    `translations[${index}].twitterDescription`,
                    e.target.value
                  )
                }
              }}
              name='twitterDescription'
              className={clsx(
                'form-control mb-3 mb-lg-0',
                {
                  'is-invalid':
                    formik.touched.translations?.[0]?.twitterDescription &&
                    (formik.errors.translations?.[0] as {twitterDescription?: string})
                      ?.twitterDescription,
                },
                {
                  'is-valid':
                    formik.touched.translations?.[0]?.twitterDescription &&
                    !(formik.errors.translations?.[0] as {twitterDescription?: string})
                      ?.twitterDescription,
                }
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || isPageLoading}
            />
          </div>

          <div className='fv-row mb-4'>
            <label className='fw-bold fs-6'>
              {intl.formatMessage({ id: 'COLUMNS.SEO_KEYWORDS' })}
            </label>
            <TagsComponent
              key={selectedLanguage + (selectedTranslation?.metaKeywords?.join() || '')}
              initialValues={selectedTranslation?.metaKeywords || []}
              onValuesChange={(updatedMetaKeywords) => {
                const index = formik.values.translations.findIndex(t => t.language === selectedLanguage);
                if (index !== -1) {
                  formik.setFieldValue(`translations[${index}].metaKeywords`, updatedMetaKeywords);
                }
              }}
              type='metaKeywords'
              selectedLanguage={selectedLanguage}
            />
          </div>
        </div>
        {/* end::Scroll */}

        {/* begin::Actions */}
        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            data-kt-Pages-modal-action='cancel'
            disabled={formik.isSubmitting || isPageLoading}
          >
            {intl.formatMessage({id: 'HEADER.RESET'})}
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            data-kt-users-modal-action='submit'
            disabled={isPageLoading || formik.isSubmitting || !formik.isValid || !formik.touched}
          >
            <span className='indicator-label'> {intl.formatMessage({id: 'HEADER.SAVE'})}</span>
            {(formik.isSubmitting || isPageLoading) && (
              <span className='indicator-progress'>
                {intl.formatMessage({id: 'COMMON.PLEASE_WAIT'})}{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Actions */}
      </form>
      {(formik.isSubmitting || isPageLoading) && <DataLoading />}
    </>
  )
}

export {PageEditModalForm}
